// src/components/MultiSelect/MultiSelect.jsx
// Reusable multi-select dropdown with checkboxes

import { useState, useRef, useEffect } from 'react';
import './MultiSelect.css';

const MultiSelect = ({
  label,
  options = [],        // [{ id, name }]
  selected = [],       // [id, id, ...]
  onChange,            // (selectedIds[]) => void
  disabled = false,
  placeholder = 'Select...',
  loading = false,
  emptyMsg = 'No options available',
}) => {
  const [open, setOpen]       = useState(false);
  const containerRef          = useRef(null);

  // Close on outside click
  useEffect(() => {
    const handler = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const toggle = (id) => {
    const numId = Number(id);
    if (selected.includes(numId)) {
      onChange(selected.filter(s => s !== numId));
    } else {
      onChange([...selected, numId]);
    }
  };

  const selectAll = () => onChange(options.map(o => o.id));
  const clearAll  = () => onChange([]);

  const selectedLabels = options
    .filter(o => selected.includes(o.id))
    .map(o => o.name);

  return (
    <div className="ms-wrapper" ref={containerRef}>
      {label && <label className="form-label">{label}</label>}

      {/* Trigger */}
      <div
        className={`ms-trigger ${open ? 'open' : ''} ${disabled ? 'disabled' : ''}`}
        onClick={() => !disabled && !loading && setOpen(prev => !prev)}
      >
        {loading ? (
          <span className="ms-placeholder">Loading...</span>
        ) : selected.length === 0 ? (
          <span className="ms-placeholder">{disabled ? placeholder : placeholder}</span>
        ) : (
          <div className="ms-tags">
            {selectedLabels.slice(0, 2).map((label, i) => (
              <span key={i} className="ms-tag">{label}</span>
            ))}
            {selectedLabels.length > 2 && (
              <span className="ms-tag ms-tag-more">+{selectedLabels.length - 2} more</span>
            )}
          </div>
        )}
        <span className="ms-arrow">{open ? '▲' : '▼'}</span>
      </div>

      {/* Dropdown */}
      {open && !disabled && (
        <div className="ms-dropdown">
          {options.length === 0 ? (
            <div className="ms-empty">{emptyMsg}</div>
          ) : (
            <>
              {/* Select all / Clear */}
              <div className="ms-actions">
                <button type="button" className="ms-action-btn" onClick={selectAll}>
                  Select All
                </button>
                <button type="button" className="ms-action-btn" onClick={clearAll}>
                  Clear
                </button>
              </div>
              <div className="ms-divider" />
              {/* Options */}
              {options.map(opt => (
                <label key={opt.id} className="ms-option">
                  <input
                    type="checkbox"
                    checked={selected.includes(opt.id)}
                    onChange={() => toggle(opt.id)}
                  />
                  <span className="ms-option-label">{opt.name}</span>
                  {opt.location && (
                    <span className="ms-option-sub">{opt.location}</span>
                  )}
                </label>
              ))}
            </>
          )}
        </div>
      )}

      {/* Selected count */}
      {selected.length > 0 && (
        <small className="ms-count">
          {selected.length} selected
        </small>
      )}
    </div>
  );
};

export default MultiSelect;